/**
 * Roots funnel rate bands for Ads Manager cell coloring.
 *
 * p1CVR (g.pCTR) — article → tile:
 *   low < 30 · med 30–50 · good ≥ 50
 *
 * p2CVR (g.CTR) — SERP → advertiser:
 *   low < 5 · med 5–12 · good ≥ 12
 *
 * Null / non-finite → no band (N/A stays default white like other columns).
 */

export type FunnelCvrBand = "good" | "med" | "low"

export function getP1CvrBand(
  value: number | null | undefined,
): FunnelCvrBand | null {
  if (value == null || !Number.isFinite(value)) return null
  if (value < 30) return "low"
  if (value < 50) return "med"
  return "good"
}

export function getP2CvrBand(
  value: number | null | undefined,
): FunnelCvrBand | null {
  if (value == null || !Number.isFinite(value)) return null
  if (value < 5) return "low"
  if (value < 12) return "med"
  return "good"
}

export function getFunnelCvrBand(
  columnId: string,
  value: unknown,
): FunnelCvrBand | null {
  if (columnId !== "p1_cvr" && columnId !== "p2_cvr") return null
  if (value == null || value === "") return null
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return columnId === "p1_cvr" ? getP1CvrBand(num) : getP2CvrBand(num)
}

/** Tailwind text class; empty string when N/A so the cell keeps default white. */
export function funnelCvrTextClass(band: FunnelCvrBand | null): string {
  switch (band) {
    case "good":
      return "text-green-400"
    case "med":
      return "text-amber-400"
    case "low":
      return "text-red-400"
    default:
      return ""
  }
}

export function funnelCvrTitle(
  columnId: string,
  band: FunnelCvrBand | null,
): string | undefined {
  if (!band) return undefined
  if (columnId === "p1_cvr") {
    if (band === "good") return "p1CVR good (≥50%) — article → tile is healthy"
    if (band === "med") return "p1CVR med (30–50%) — article/chips soft"
    return "p1CVR low (<30%) — article or chips underperforming"
  }
  if (columnId === "p2_cvr") {
    if (band === "good") return "p2CVR good (≥12%) — SERP has advertisers"
    if (band === "med") return "p2CVR med (5–12%) — auction thinning"
    return "p2CVR low (<5%) — Zero Coverage risk"
  }
  return undefined
}
